import { api } from '../api';
import { guardarEEnviar } from '../fila';

/**
 * Instrumentação e monitoramento — as chamadas, com o caminho escrito.
 *
 * Mesma razão de `api/campo.ts`: o caminho é o contrato entre a tela e a
 * API, e o teste `26` procura o literal aqui. Caminho montado a partir de
 * sufixo existe só em tempo de execução, e o que não se acha procurando
 * não se confere.
 */

export interface TipoDeInstrumento {
  codigo: string;
  nome: string;
  familia: string;
  unidade: string | null;
  observacoes: string | null;
}

export interface Instrumento {
  id: string;
  ponto_investigacao_id: string;
  ponto_codigo: string;
  os_id: string;
  os_numero: string;
  obra_codigo: string | null;
  obra_nome: string | null;
  cliente: string | null;
  latitude: string | null;
  longitude: string | null;
  codigo: string;
  tipo: string;
  tipo_nome: string;
  familia: string;
  unidade_leitura: string | null;
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  data_instalacao: string;
  instalado_por_nome: string | null;
  cota_boca: string | null;
  cota_referencia: string | null;
  profundidade_instalacao: string | null;
  profundidade_total: string | null;
  situacao: string;
  motivo_situacao: string | null;
  observacoes: string | null;
  leituras: number;
  ultima_leitura: string | null;
  leituras_a_conferir: number;
  nivel_sem_cota_boca: boolean;
  recalque_sem_referencia: boolean;
}

export interface Leitura {
  id: string;
  instrumento_id: string;
  instrumento_codigo: string;
  familia: string;
  unidade: string | null;
  cota_boca: string | null;
  ponto_codigo: string;
  os_numero: string;
  obra_nome: string | null;
  data: string;
  hora: string | null;
  seco: boolean | null;
  profundidade_nivel: string | null;
  cota_lida: string | null;
  referencia: boolean;
  temperatura_c: string | null;
  observacoes: string | null;
  lida_por_nome: string | null;
  situacao: string;
  conferida_por_nome: string | null;
  conferida_em: string | null;
  motivo_rejeicao: string | null;
  /** cota da boca − profundidade lida. Aritmética, não critério. */
  cota_nivel: string | null;
  /** (cota da leitura zero − cota lida) × 1000, positivo para baixo. */
  recalque_mm: string | null;
  data_zero: string | null;
  /** Variação para a leitura anterior, já na unidade da família. */
  variacao: string | null;
  /** «m» no nível, «mm» no recalque — o cabeçalho não deduz. */
  unidade_variacao: string;
  dias_desde_a_anterior: number | null;
}

export interface Pendencia {
  id: string;
  codigo: string;
  tipo_nome: string;
  familia: string;
  obra_nome: string | null;
  os_numero: string;
  leituras: number;
  ultima_leitura: string | null;
  leituras_a_conferir: number;
  falta: string;
}

export const tiposDeInstrumento = () =>
  api<TipoDeInstrumento[]>('/instrumentos/tipos');

export const verInstrumentos = (p: {
  os?: string; obra?: string; pendentes?: boolean;
} = {}) => {
  const q = new URLSearchParams();
  if (p.os) q.set('os', p.os);
  if (p.obra) q.set('obra', p.obra);
  if (p.pendentes) q.set('pendentes', '1');
  const s = q.toString();
  return api<Instrumento[]>(`/instrumentos${s ? `?${s}` : ''}`);
};

export const instrumentosPendentes = () =>
  api<Pendencia[]>('/instrumentos/pendentes');

export const verInstrumento = (id: string) =>
  api<{ instrumento: Instrumento; leituras: Leitura[] }>(
    `/instrumentos/${id}`);

/** Instalar — o instrumento nasce no ponto que o recebeu. */
export const instalarInstrumento = (pontoId: string, corpo: {
  codigo: string; tipo: string; dataInstalacao: string;
  fabricante?: string; modelo?: string; numeroSerie?: string;
  cotaBoca?: number | null; cotaReferencia?: string;
  profundidadeInstalacao?: number | null;
  profundidadeTotal?: number | null; observacoes?: string;
}) => api<{ id: string; codigo: string; situacao: string }>(
  `/pontos/${pontoId}/instrumentos`, { metodo: 'POST', corpo });

export const alterarInstrumento = (id: string, corpo: Record<string, unknown>) =>
  api<{ id: string; codigo: string }>(`/instrumentos/${id}`,
    { metodo: 'PATCH', corpo });

/** O instrumento parou — e o motivo é obrigatório fora de «ativo». */
export const situacaoDoInstrumento = (id: string, corpo: {
  situacao: string; motivo?: string;
}) => api<{ id: string; situacao: string }>(`/instrumentos/${id}/situacao`,
  { metodo: 'POST', corpo });

/**
 * Lançar uma leitura — pela FILA (onda 127).
 *
 * A campanha de monitoramento é o caso off-line por excelência: vinte
 * instrumentos numa barragem, sem sinal, uma tarde inteira. `guardarEEnviar`
 * grava no aparelho ANTES de tentar a rede, e só tira de lá quando o
 * servidor confirma — o que faz o técnico voltar do campo com os vinte
 * lançamentos, e não com a memória deles.
 *
 * O caminho continua escrito aqui, literal, porque o teste `26` procura
 * por ele: a fila é um transporte diferente, não um contrato diferente.
 */
export const lancarLeitura = (id: string, corpo: {
  osId: string; data: string; lidaPor: string;
  diarioId?: string | null; hora?: string | null;
  seco?: boolean | null; profundidadeNivel?: number | null;
  cotaLida?: number | null; referencia?: boolean;
  temperaturaC?: number | null; observacoes?: string;
}, rotulo?: string) => guardarEEnviar<{
  id: string; data: string; situacao: string;
}>(`/instrumentos/${id}/leituras`, corpo,
  /*
   * O rótulo é o que a janela da fila mostra, e ele tem de identificar
   * O QUE ficou guardado — «Leitura de instrumento» não distingue o
   * PZ-01 do MS-07 numa campanha de vinte, que é justamente a situação
   * em que a fila enche. Quem sabe o código é a tela, e por isso ela
   * passa o rótulo; o padrão daqui é a rede de segurança.
   */
  { rotulo: rotulo ?? `Leitura de instrumento · ${emPtBr(corpo.data)}` });

/** Data ISO no padrão brasileiro (§3). O rótulo é texto para gente. */
const emPtBr = (iso: string) =>
  String(iso).slice(0, 10).split('-').reverse().join('/');

/** Conferida, a leitura não muda mais de valor — quem recusa é o banco. */
export const conferirLeitura = (id: string, corpo: {
  situacao: 'conferida' | 'rejeitada'; motivo?: string;
}) => api<{ id: string; situacao: string }>(`/leituras/${id}/conferir`,
  { metodo: 'POST', corpo });
